import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, PlayIcon, SaveIcon, DownloadIcon, UploadIcon, ZoomInIcon, ZoomOutIcon, RotateCwIcon, CheckCircleIcon, XCircleIcon, ClockIcon, FileTextIcon, LayersIcon, BoxIcon, ArrowRightIcon, UserIcon, UsersIcon, BellIcon, AlertCircleIcon, ToggleLeftIcon, ToggleRightIcon, AlertTriangleIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from '../sharedFinancialData';
// Mock data types
type WorkflowDefinition = {
  id: string;
  name: string;
  description: string;
  documentType: string;
  version: string;
  status: 'Active' | 'Draft' | 'Inactive';
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  bpmnXml: string;
  createdAt: string;
  updatedAt: string;
  lastPublishedAt?: string;
  isDefault: boolean;
};
type DocumentType = {
  id: string;
  name: string;
  description: string;
  category: string;
};
export function WorkflowManager({
  locale
}) {
  const [activeView, setActiveView] = useState<'list' | 'edit' | 'preview'>('list');
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDefinition | null>(null);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDocumentType, setFilterDocumentType] = useState<string>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Mock data for document types
  const documentTypes: DocumentType[] = [{
    id: 'invoice',
    name: 'Invoice',
    description: 'Accounts payable invoice',
    category: 'AP'
  }, {
    id: 'po',
    name: 'Purchase Order',
    description: 'Procurement purchase order',
    category: 'Procurement'
  }, {
    id: 'requisition',
    name: 'Requisition',
    description: 'Purchase requisition',
    category: 'Procurement'
  }, {
    id: 'vendor',
    name: 'Vendor',
    description: 'Vendor record',
    category: 'Vendor Management'
  }, {
    id: 'credit-note',
    name: 'Credit Note',
    description: 'AP credit note',
    category: 'AP'
  }, {
    id: 'receipt',
    name: 'Goods Receipt',
    description: 'Goods receipt document',
    category: 'Procurement'
  }, {
    id: 'expense',
    name: 'Expense Claim',
    description: 'Employee expense claim',
    category: 'Expenses'
  }, {
    id: 'contract',
    name: 'Contract',
    description: 'Vendor contract',
    category: 'Contract Management'
  }];
  // Mock data for workflows
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([{
    id: 'wf-001',
    name: 'Invoice Approval Workflow',
    description: 'Standard invoice approval process with delegation thresholds',
    documentType: 'invoice',
    version: '1.2',
    status: 'Active',
    scope: 'Global',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-03-10T14:30:00Z',
    lastPublishedAt: '2023-03-10T14:30:00Z',
    isDefault: true
  }, {
    id: 'wf-002',
    name: 'Purchase Order Approval',
    description: 'Approval workflow for purchase orders with budget validation',
    documentType: 'po',
    version: '1.0',
    status: 'Active',
    scope: 'Global',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-02-01T09:15:00Z',
    updatedAt: '2023-02-15T11:20:00Z',
    lastPublishedAt: '2023-02-15T11:20:00Z',
    isDefault: true
  }, {
    id: 'wf-003',
    name: 'Singapore High-Value Invoice',
    description: 'Special approval process for high-value invoices in Singapore',
    documentType: 'invoice',
    version: '1.0',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-04-20T13:30:00Z',
    updatedAt: '2023-04-20T13:30:00Z',
    lastPublishedAt: '2023-04-20T13:30:00Z',
    isDefault: false
  }, {
    id: 'wf-004',
    name: 'Mexico PO Approval',
    description: 'Purchase order approval workflow for Mexico operations',
    documentType: 'po',
    version: '1.1',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-03-01T10:20:00Z',
    updatedAt: '2023-05-12T09:45:00Z',
    lastPublishedAt: '2023-05-12T09:45:00Z',
    isDefault: false
  }]);
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Handler for creating a new workflow
  const handleCreateWorkflow = () => {
    const newWorkflow: WorkflowDefinition = {
      id: `wf-${new Date().getTime()}`,
      name: 'New Workflow',
      description: '',
      documentType: '',
      version: '0.1',
      status: 'Draft',
      scope: 'Global',
      bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDefault: false
    };
    setSelectedWorkflow(newWorkflow);
    setActiveView('edit');
  };
  // Handler for editing a workflow
  const handleEditWorkflow = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setActiveView('edit');
  };
  // Handler for previewing a workflow
  const handlePreviewWorkflow = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setActiveView('preview');
    setZoomLevel(100); // Reset zoom level
  };
  // Handler for duplicating a workflow
  const handleDuplicateWorkflow = (workflow: WorkflowDefinition) => {
    const duplicatedWorkflow: WorkflowDefinition = {
      ...workflow,
      id: `wf-${new Date().getTime()}`,
      name: `Copy of ${workflow.name}`,
      status: 'Draft',
      version: '0.1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastPublishedAt: undefined,
      isDefault: false
    };
    setWorkflows([...workflows, duplicatedWorkflow]);
  };
  // Handler for activating/deactivating a workflow
  const handleToggleWorkflowStatus = (workflowId: string) => {
    setWorkflows(workflows.map(wf => wf.id === workflowId ? {
      ...wf,
      status: wf.status === 'Active' ? 'Inactive' : wf.status === 'Inactive' ? 'Active' : wf.status,
      updatedAt: new Date().toISOString(),
      ...(wf.status === 'Inactive' ? {
        lastPublishedAt: new Date().toISOString()
      } : {})
    } : wf));
  };
  // Handler for publishing a draft workflow
  const handlePublishWorkflow = (workflowId: string) => {
    setWorkflows(workflows.map(wf => wf.id === workflowId ? {
      ...wf,
      status: 'Active',
      updatedAt: new Date().toISOString(),
      lastPublishedAt: new Date().toISOString()
    } : wf));
  };
  // Handler for deleting a workflow
  const handleDeleteWorkflow = (workflowId: string) => {
    setDeletingId(workflowId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setWorkflows(workflows.filter(wf => wf.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handler for setting a workflow as default for its document type
  const handleSetAsDefault = (workflow: WorkflowDefinition) => {
    setWorkflows(workflows.map(wf => wf.documentType === workflow.documentType ? {
      ...wf,
      isDefault: wf.id === workflow.id,
      updatedAt: wf.id === workflow.id ? new Date().toISOString() : wf.updatedAt
    } : wf));
  };
  // Handler for saving a workflow
  const handleSaveWorkflow = (updatedWorkflow: WorkflowDefinition) => {
    if (updatedWorkflow.id && workflows.find(w => w.id === updatedWorkflow.id)) {
      // Update existing workflow
      setWorkflows(workflows.map(wf => wf.id === updatedWorkflow.id ? {
        ...updatedWorkflow,
        updatedAt: new Date().toISOString()
      } : wf));
    } else {
      // Create new workflow
      const newWorkflow = {
        ...updatedWorkflow,
        id: updatedWorkflow.id || `wf-${new Date().getTime()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setWorkflows([...workflows, newWorkflow]);
    }
    setActiveView('list');
    setSelectedWorkflow(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    if (selectedWorkflow) {
      setSelectedWorkflow({
        ...selectedWorkflow,
        orgUnitId: orgUnitId,
        orgUnitName: orgUnit?.name || '',
        orgUnitPath: orgUnit?.path || ''
      });
    }
    setSelectedOrgUnit(orgUnit);
  };
  // Filter workflows based on search term and document type filter
  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) || workflow.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDocumentType = filterDocumentType === 'all' || workflow.documentType === filterDocumentType;
    return matchesSearch && matchesDocumentType;
  });
  // Get document type name from ID
  const getDocumentTypeName = (id: string) => {
    const docType = documentTypes.find(dt => dt.id === id);
    return docType ? docType.name : id;
  };
  const globalWorkflows = filteredWorkflows.filter(w => w.scope === 'Global');
  const orgUnitWorkflows = filteredWorkflows.filter(w => w.scope === 'OrgUnit');
  // Render the workflows list
  const renderWorkflowsList = () => {
    return <div className="space-y-4" data-id="element-13111">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-4 sm:space-y-0" data-id="element-13112">
          <div data-id="element-13113">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-13114">
              Workflow Definitions
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-13115">
              Manage business process workflows at global and organisation unit
              levels
            </p>
          </div>
          <button onClick={handleCreateWorkflow} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13116">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-13117" />
            Create Workflow
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-4" data-id="element-13118">
          <div className="relative rounded-md shadow-sm flex-1" data-id="element-13119">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-10 py-2 sm:text-sm border-gray-300 rounded-md" placeholder="Search workflows..." data-id="element-13120" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-13121">
              <FileTextIcon className="h-4 w-4 text-gray-400" data-id="element-13122" />
            </div>
          </div>
          <div className="sm:w-64" data-id="element-13123">
            <select value={filterDocumentType} onChange={e => setFilterDocumentType(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-13124">
              <option value="all" data-id="element-13125">All Document Types</option>
              {documentTypes.map(docType => <option key={docType.id} value={docType.id} data-id="element-13126">
                  {docType.name}
                </option>)}
            </select>
          </div>
        </div>

        {/* Global Workflows */}
        <div className="bg-white shadow rounded-lg mb-6" data-id="element-13127">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-13128">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13129">
              Global Workflows
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-13130">
              Global workflows available to all organisation units
            </p>
          </div>

          {globalWorkflows.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-13131">
              <p className="text-sm text-gray-500" data-id="element-13132">
                No global workflows found.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-13133">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-13134">
                <thead className="bg-gray-50" data-id="element-13135">
                  <tr data-id="element-13136">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13137">
                      Workflow
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13138">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13139">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13140">
                      Version
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13141">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-13142">
                  {globalWorkflows.map(workflow => <tr key={workflow.id} data-id="element-13143">
                      <td className="px-6 py-4" data-id="element-13144">
                        <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-13145">
                          {workflow.name}
                          {workflow.isDefault && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-13146">
                              Default
                            </span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-13147">
                          {workflow.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-13148">
                        {getDocumentTypeName(workflow.documentType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-13149">
                        {workflow.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-13150" />}
                        {workflow.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-13151" />}
                        {workflow.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-13152" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-13153">
                        v{workflow.version}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-13154">
                        <div className="flex justify-end space-x-2" data-id="element-13155">
                          <button onClick={() => handleEditWorkflow(workflow)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-13156">
                            <EditIcon className="h-4 w-4" data-id="element-13157" />
                          </button>
                          <button onClick={() => handlePreviewWorkflow(workflow)} className="text-blue-600 hover:text-blue-900" title="Preview" data-id="element-13158">
                            <PlayIcon className="h-4 w-4" data-id="element-13159" />
                          </button>
                          <button onClick={() => handleDuplicateWorkflow(workflow)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-13160">
                            <CopyIcon className="h-4 w-4" data-id="element-13161" />
                          </button>
                          {workflow.status === 'Draft' && <button onClick={() => handlePublishWorkflow(workflow.id)} className="text-green-600 hover:text-green-900" title="Publish" data-id="element-13162">
                              <CheckCircleIcon className="h-4 w-4" data-id="element-13163" />
                            </button>}
                          <button onClick={() => handleDeleteWorkflow(workflow.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-13164">
                            <TrashIcon className="h-4 w-4" data-id="element-13165" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>

        {/* OrgUnit Workflows */}
        <div className="bg-white shadow rounded-lg" data-id="element-13166">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-13167">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13168">
              Organisation Unit Workflows
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-13169">
              Workflows specific to individual organisation units
            </p>
          </div>

          {orgUnitWorkflows.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-13170">
              <p className="text-sm text-gray-500" data-id="element-13171">
                No organisation unit workflows found.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-13172">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-13173">
                <thead className="bg-gray-50" data-id="element-13174">
                  <tr data-id="element-13175">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13176">
                      Organisation Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13177">
                      Workflow
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13178">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13179">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13180">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-13181">
                  {orgUnitWorkflows.map(workflow => <tr key={workflow.id} data-id="element-13182">
                      <td className="px-6 py-4" data-id="element-13183">
                        <div className="text-sm font-medium text-gray-900" data-id="element-13184">
                          {workflow.orgUnitName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-13185">
                          {workflow.orgUnitPath}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-13186">
                        <div className="text-sm font-medium text-gray-900" data-id="element-13187">
                          {workflow.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-13188">
                          {workflow.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-13189">
                        {getDocumentTypeName(workflow.documentType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-13190">
                        {workflow.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-13191" />}
                        {workflow.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-13192" />}
                        {workflow.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-13193" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-13194">
                        <div className="flex justify-end space-x-2" data-id="element-13195">
                          <button onClick={() => handleEditWorkflow(workflow)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-13196">
                            <EditIcon className="h-4 w-4" data-id="element-13197" />
                          </button>
                          <button onClick={() => handlePreviewWorkflow(workflow)} className="text-blue-600 hover:text-blue-900" title="Preview" data-id="element-13198">
                            <PlayIcon className="h-4 w-4" data-id="element-13199" />
                          </button>
                          <button onClick={() => handleDuplicateWorkflow(workflow)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-13200">
                            <CopyIcon className="h-4 w-4" data-id="element-13201" />
                          </button>
                          {workflow.status === 'Draft' && <button onClick={() => handlePublishWorkflow(workflow.id)} className="text-green-600 hover:text-green-900" title="Publish" data-id="element-13202">
                              <CheckCircleIcon className="h-4 w-4" data-id="element-13203" />
                            </button>}
                          <button onClick={() => handleDeleteWorkflow(workflow.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-13204">
                            <TrashIcon className="h-4 w-4" data-id="element-13205" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  };
  // Render the workflow editor (simplified - keeping existing complex logic)
  const renderWorkflowEditor = () => {
    if (!selectedWorkflow) return null;
    return <div className="space-y-6" data-id="element-13206">
        <div className="flex justify-between items-center mb-4" data-id="element-13207">
          <button onClick={() => {
          setActiveView('list');
          setSelectedWorkflow(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13208">
            ← Back to Workflows
          </button>
          <div className="flex space-x-2" data-id="element-13209">
            <button onClick={() => handleSaveWorkflow(selectedWorkflow)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13210">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-13211" />
              Save
            </button>
          </div>
        </div>

        {/* Workflow details form */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13212">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-13213">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13214">
              Workflow Details
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-13215">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-13216">
              {/* Scope */}
              <div className="sm:col-span-3" data-id="element-13217">
                <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-13218">
                  Scope <span className="text-red-500" data-id="element-13219">*</span>
                </label>
                <div className="mt-1" data-id="element-13220">
                  <select id="scope" value={selectedWorkflow.scope} onChange={e => {
                  const newScope = e.target.value as 'Global' | 'OrgUnit';
                  setSelectedWorkflow({
                    ...selectedWorkflow,
                    scope: newScope,
                    ...(newScope === 'Global' ? {
                      orgUnitId: undefined,
                      orgUnitName: undefined,
                      orgUnitPath: undefined
                    } : {})
                  });
                }} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13221">
                    <option value="Global" data-id="element-13222">Global</option>
                    <option value="OrgUnit" data-id="element-13223">Organisation Unit</option>
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-13224">
                  {selectedWorkflow.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                </p>
              </div>

              {/* OrgUnit Selector */}
              {selectedWorkflow.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-13225">
                  <OrgUnitSelector value={selectedWorkflow.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-13226" />
                </div>}

              <div className="sm:col-span-3" data-id="element-13227">
                <label htmlFor="workflow-name" className="block text-sm font-medium text-gray-700" data-id="element-13228">
                  Name
                </label>
                <div className="mt-1" data-id="element-13229">
                  <input type="text" id="workflow-name" value={selectedWorkflow.name} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  name: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13230" />
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-13231">
                <label htmlFor="document-type" className="block text-sm font-medium text-gray-700" data-id="element-13232">
                  Document Type
                </label>
                <div className="mt-1" data-id="element-13233">
                  <select id="document-type" value={selectedWorkflow.documentType} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  documentType: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13234">
                    <option value="" data-id="element-13235">Select Document Type</option>
                    {documentTypes.map(docType => <option key={docType.id} value={docType.id} data-id="element-13236">
                        {docType.name}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="sm:col-span-6" data-id="element-13237">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-13238">
                  Description
                </label>
                <div className="mt-1" data-id="element-13239">
                  <textarea id="description" rows={3} value={selectedWorkflow.description} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  description: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13240" />
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-13241">
                <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-13242">
                  Version
                </label>
                <div className="mt-1" data-id="element-13243">
                  <input type="text" id="version" value={selectedWorkflow.version} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  version: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13244" />
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-13245">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-13246">
                  Status
                </label>
                <div className="mt-1" data-id="element-13247">
                  <select id="status" value={selectedWorkflow.status} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  status: e.target.value as 'Active' | 'Draft' | 'Inactive'
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13248">
                    <option value="Draft" data-id="element-13249">Draft</option>
                    <option value="Active" data-id="element-13250">Active</option>
                    <option value="Inactive" data-id="element-13251">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-13252">
                <label htmlFor="default" className="block text-sm font-medium text-gray-700" data-id="element-13253">
                  Default Workflow
                </label>
                <div className="mt-1" data-id="element-13254">
                  <select id="default" value={selectedWorkflow.isDefault ? 'yes' : 'no'} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  isDefault: e.target.value === 'yes'
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13255">
                    <option value="no" data-id="element-13256">No</option>
                    <option value="yes" data-id="element-13257">Yes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BPMN Diagram Editor Placeholder */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13258">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-13259">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13260">
              Workflow Diagram
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-13261">
            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg text-center" data-id="element-13262">
              <LayersIcon className="h-12 w-12 text-gray-400" data-id="element-13263" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-13264">
                BPMN Workflow Editor
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-13265">
                The BPMN editor would be integrated here to visually design the
                workflow
              </p>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render preview (keeping simplified version)
  const renderWorkflowPreview = () => {
    if (!selectedWorkflow) return null;
    return <div className="space-y-6" data-id="element-13266">
        <div className="flex justify-between items-center mb-4" data-id="element-13267">
          <button onClick={() => {
          setActiveView('list');
          setSelectedWorkflow(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-13268">
            ← Back to Workflows
          </button>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13269">
          <div className="px-4 py-5 sm:px-6" data-id="element-13270">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13271">
              {selectedWorkflow.name}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-13272">
              {selectedWorkflow.description}
            </p>
          </div>
        </div>
      </div>;
  };
  // Main render function
  return <div className="space-y-6" data-id="element-13273">
      <div className="mb-8" data-id="element-13274">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-13275">Workflows</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-13276">
          Design and manage business process workflows with BPMN visualization
        </p>
      </div>

      {/* Main Content */}
      {activeView === 'list' ? renderWorkflowsList() : activeView === 'edit' ? renderWorkflowEditor() : renderWorkflowPreview()}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-13277">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-13278">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-13279">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-13280"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-13281">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-13282">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-13283">
                <div className="sm:flex sm:items-start" data-id="element-13284">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-13285">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-13286" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-13287">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13288">
                      Delete Workflow
                    </h3>
                    <div className="mt-2" data-id="element-13289">
                      <p className="text-sm text-gray-500" data-id="element-13290">
                        Are you sure you want to delete this workflow? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-13291">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-13292">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-13293">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}